import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ChevronLeft, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/field";
import type { CommunityEvent, LocaleCode, LocalizedText } from "@/shared/types";
import { recaptchaToken } from "@/lib/recaptcha";

const text = (value: LocalizedText, locale: LocaleCode) =>
  value[locale] || value["en-US"];

interface VerifiedProfile {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  sms_consent: boolean;
  email_consent: boolean;
}

export function EventRegistrationPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const locale: LocaleCode = i18n.language.startsWith("es") ? "es-US" : "en-US";
  const [event, setEvent] = useState<CommunityEvent | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [lookupChannel, setLookupChannel] = useState<"email" | "sms">("email");
  const [lookupContact, setLookupContact] = useState("");
  const [lookupCode, setLookupCode] = useState("");
  const [lookupRequestId, setLookupRequestId] = useState("");
  const [lookupMessage, setLookupMessage] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [verifiedProfile, setVerifiedProfile] =
    useState<VerifiedProfile | null>(null);
  useEffect(() => {
    if (slug)
      void api<CommunityEvent>(`/public/events/${slug}`)
        .then(setEvent)
        .catch((e) => setError(e.message));
  }, [slug]);

  const requestLookupCode = async () => {
    setError("");
    setLookupBusy(true);
    try {
      const protectionToken = await recaptchaToken("profile_lookup");
      const response = await api<{ request_id: string; message: string }>(
        "/public/profiles/verification-requests",
        {
          method: "POST",
          body: JSON.stringify({
            channel: lookupChannel,
            contact: lookupContact,
            locale,
            recaptcha_token: protectionToken,
          }),
        },
      );
      setLookupRequestId(response.request_id);
      setLookupMessage(response.message);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : t("events.lookupError"),
      );
    } finally {
      setLookupBusy(false);
    }
  };

  const verifyLookupCode = async () => {
    setError("");
    setLookupBusy(true);
    try {
      const response = await api<{ profile: VerifiedProfile }>(
        "/public/profiles/verification",
        {
          method: "POST",
          body: JSON.stringify({
            request_id: lookupRequestId,
            code: lookupCode,
          }),
        },
      );
      setVerifiedProfile(response.profile);
      setLookupMessage(t("events.profileLoaded"));
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : t("events.lookupError"),
      );
    } finally {
      setLookupBusy(false);
    }
  };

  const submit = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    if (!event) return;
    const data = new FormData(formEvent.currentTarget);
    const profile = {
      first_name: data.get("first_name"),
      last_name: data.get("last_name"),
      email: data.get("email"),
      phone: data.get("phone"),
      preferred_locale: locale,
      relationship: "neighbor",
      location_id: event.location_id,
      sms_consent: data.get("sms_consent") === "on",
      email_consent: data.get("email_consent") === "on",
      consent_source: `event:${event.id}`,
    };
    const answers = Object.fromEntries(
      event.questions.map((question) => [question.id, data.get(question.id)]),
    );
    try {
      const protectionToken = await recaptchaToken("event_registration");
      await api(`/public/events/${event.slug}/registrations`, {
        method: "POST",
        body: JSON.stringify({
          locale,
          profile,
          party_size: Number(data.get("party_size")),
          answers,
          recaptcha_token: protectionToken,
        }),
      });
      setSuccess(true);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Unable to register.",
      );
    }
  };

  if (success)
    return (
      <div className="mx-auto max-w-xl px-5 py-20 text-center">
        <CheckCircle2 className="mx-auto text-primary" size={56} />
        <h1 className="mt-6 text-3xl font-bold">{t("events.success")}</h1>
        <Button asChild className="mt-8" variant="outline">
          <Link to="/events">{t("common.back")}</Link>
        </Button>
      </div>
    );
  return (
    <div className="mx-auto max-w-3xl px-5 py-12">
      <Link
        to="/events"
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground"
      >
        <ChevronLeft size={16} />
        {t("common.back")}
      </Link>
      <Card className="shadow-soft">
        <CardHeader className="border-b bg-primary text-primary-foreground">
          <CardTitle className="text-3xl">
            {event ? text(event.title, locale) : t("form.loading")}
          </CardTitle>
          {event && (
            <p className="text-primary-foreground/75">
              {text(event.description, locale)}
            </p>
          )}
        </CardHeader>
        <CardContent className="p-7 sm:p-10">
          {error && (
            <p
              role="alert"
              className="mb-5 rounded-lg bg-red-50 p-4 text-red-800"
            >
              {error}
            </p>
          )}
          {event && (
            <>
              <section className="mb-8 rounded-xl border bg-secondary/35 p-5 sm:p-6">
                <div className="flex gap-3">
                  <ShieldCheck className="mt-0.5 shrink-0" size={22} />
                  <div>
                    <h2 className="font-semibold">
                      {t("events.returningTitle")}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t("events.returningBody")}
                    </p>
                  </div>
                </div>
                {!verifiedProfile && (
                  <div className="mt-5 grid gap-3 sm:grid-cols-[auto_1fr_auto]">
                    <div className="flex rounded-lg border bg-background p-1">
                      {(["email", "sms"] as const).map((channel) => (
                        <button
                          key={channel}
                          type="button"
                          onClick={() => {
                            setLookupChannel(channel);
                            setLookupRequestId("");
                            setLookupMessage("");
                          }}
                          className={`rounded-md px-3 py-2 text-sm font-medium ${lookupChannel === channel ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                        >
                          {t(`events.${channel}`)}
                        </button>
                      ))}
                    </div>
                    <Input
                      aria-label={t(`events.${lookupChannel}`)}
                      type={lookupChannel === "email" ? "email" : "tel"}
                      value={lookupContact}
                      onChange={(event) => setLookupContact(event.target.value)}
                      placeholder={t(`events.${lookupChannel}Placeholder`)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={lookupBusy || lookupContact.trim().length < 3}
                      onClick={() => void requestLookupCode()}
                    >
                      {t("events.sendCode")}
                    </Button>
                  </div>
                )}
                {lookupRequestId && !verifiedProfile && (
                  <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
                    <Input
                      aria-label={t("events.code")}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={lookupCode}
                      onChange={(event) =>
                        setLookupCode(event.target.value.replace(/\D/g, ""))
                      }
                      placeholder={t("events.codePlaceholder")}
                    />
                    <Button
                      type="button"
                      disabled={lookupBusy || lookupCode.length !== 6}
                      onClick={() => void verifyLookupCode()}
                    >
                      {t("events.verifyCode")}
                    </Button>
                  </div>
                )}
                {lookupMessage && (
                  <p className="mt-3 text-sm" role="status">
                    {lookupMessage}
                  </p>
                )}
              </section>
              <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
                {[
                  ["first_name", locale === "es-US" ? "Nombre" : "First name"],
                  ["last_name", locale === "es-US" ? "Apellido" : "Last name"],
                  ["phone", locale === "es-US" ? "Teléfono" : "Mobile phone"],
                  [
                    "email",
                    locale === "es-US" ? "Correo electrónico" : "Email address",
                  ],
                ].map(([name, label]) => (
                  <div key={name}>
                    <Label htmlFor={name} className="mb-2 block">
                      {label}
                      {name === "first_name" || name === "last_name"
                        ? " *"
                        : ""}
                    </Label>
                    <Input
                      key={`${name}-${verifiedProfile?.id || "new"}`}
                      id={name}
                      name={name}
                      type={
                        name === "email"
                          ? "email"
                          : name === "phone"
                            ? "tel"
                            : "text"
                      }
                      required={name === "first_name" || name === "last_name"}
                      defaultValue={
                        verifiedProfile?.[
                          name as "first_name" | "last_name" | "email" | "phone"
                        ] || ""
                      }
                    />
                  </div>
                ))}
                <div>
                  <Label htmlFor="party_size" className="mb-2 block">
                    {t("events.party")} *
                  </Label>
                  <Input
                    id="party_size"
                    name="party_size"
                    type="number"
                    min="1"
                    max="30"
                    required
                  />
                </div>
                {event.questions.map((question) => (
                  <div key={question.id}>
                    <Label htmlFor={question.id} className="mb-2 block">
                      {text(question.label, locale)}
                      {question.required && " *"}
                    </Label>
                    <Input
                      id={question.id}
                      name={question.id}
                      required={question.required}
                    />
                  </div>
                ))}
                <label className="flex gap-3 text-sm sm:col-span-2">
                  <input
                    name="sms_consent"
                    type="checkbox"
                    key={`sms-${verifiedProfile?.id || "new"}`}
                    defaultChecked={verifiedProfile?.sms_consent}
                    className="h-4 w-4 accent-primary"
                  />
                  {locale === "es-US"
                    ? "Envíame actualizaciones por mensaje de texto"
                    : "Text me updates about this event"}
                </label>
                <label className="flex gap-3 text-sm sm:col-span-2">
                  <input
                    name="email_consent"
                    type="checkbox"
                    key={`email-${verifiedProfile?.id || "new"}`}
                    defaultChecked={verifiedProfile?.email_consent}
                    className="h-4 w-4 accent-primary"
                  />
                  {locale === "es-US"
                    ? "Envíame actualizaciones por correo electrónico"
                    : "Email me updates about this event"}
                </label>
                <div className="sm:col-span-2">
                  <Button size="lg" type="submit">
                    {t("events.register")}
                  </Button>
                </div>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
